export type MockPage = {
  id: string;
  name: string;
  tag: string;
  color: string;
  new: number;
  learn: number;
  due: number;
  total: number;
  progress: number;
  studied: string;
  meta: string;
};

export const mockPages: MockPage[] = [
  {
    id: 'd1',
    name: 'Kanji — JLPT N3',
    tag: 'Japanese',
    color: '#3B2AFF',
    new: 12,
    learn: 4,
    due: 28,
    total: 412,
    progress: 0.68,
    studied: '2h ago',
    meta: 'Japanese · Vocabulary',
  },
  {
    id: 'd2',
    name: 'Clinical Pharmacology',
    tag: 'Medicine',
    color: '#0E8C4A',
    new: 0,
    learn: 9,
    due: 14,
    total: 236,
    progress: 0.82,
    studied: 'yesterday',
    meta: 'Medicine · Board prep',
  },
  {
    id: 'd3',
    name: 'Trigonometry Identities',
    tag: 'Math',
    color: '#C46A00',
    new: 5,
    learn: 2,
    due: 7,
    total: 64,
    progress: 0.45,
    studied: '3d ago',
    meta: 'Math · High school',
  },
  {
    id: 'd4',
    name: 'French — Conversational',
    tag: 'Language',
    color: '#B0331C',
    new: 8,
    learn: 1,
    due: 3,
    total: 182,
    progress: 0.91,
    studied: 'today',
    meta: 'Language · B2',
  },
  {
    id: 'd5',
    name: 'World History 1800–1945',
    tag: 'History',
    color: '#45464B',
    new: 2,
    learn: 0,
    due: 11,
    total: 94,
    progress: 0.33,
    studied: '5d ago',
    meta: 'History · Semester prep',
  },
];

export type MockActivity = {
  id: string;
  kind: 'review' | 'page' | 'ai' | 'deck' | 'card';
  text: string;
  when: string;
};

export const mockActivity: MockActivity[] = [
  { id: 'a1', kind: 'review', text: 'Reviewed 42 cards in Kanji — JLPT N3', when: '2h ago' },
  { id: 'a2', kind: 'ai', text: 'Generated 11 cards from Clinical Pharmacology notes', when: '3h ago' },
  { id: 'a3', kind: 'page', text: 'Edited "Beta-blocker mechanisms"', when: '5h ago' },
  { id: 'a4', kind: 'deck', text: 'Created deck "French — Conversational"', when: 'yesterday' },
  { id: 'a5', kind: 'card', text: 'Added 7 cards to Trigonometry Identities', when: '2d ago' },
  { id: 'a6', kind: 'review', text: 'Finished 120-card session', when: '3d ago' },
];

export const mockHeatmap: number[] = Array.from({ length: 16 * 7 }, (_, i) => {
  const seed = Math.sin(i * 137.2) * 10000;
  const v = Math.floor((seed - Math.floor(seed)) * 5);
  return v;
});

export const mockStats = {
  dueToday: 73,
  studied: 248,
  streak: 12,
  retention: 0.91,
};

export const mockUser = {
  name: 'Alek',
  email: 'aloksinghjsr@gmail.com',
  plan: 'Pro',
  credits: 1874,
};

export type MockReviewCard = {
  id: string;
  pageId: string;
  pageName: string;
  position: number;
  total: number;
  kanji: string;
  prompt: string;
  hint?: string;
  readings: { label: string; jp: string; romaji: string; gloss: string }[];
  example?: { jp: string; translation: string };
  memoryAid?: string;
};

export const mockPageDoc = {
  type: 'doc',
  content: [
    {
      type: 'heading',
      attrs: { level: 2 },
      content: [{ type: 'text', text: 'Autonomic nervous system — quick map' }],
    },
    {
      type: 'paragraph',
      content: [
        { type: 'text', text: 'The autonomic nervous system splits into two branches — ' },
        { type: 'text', marks: [{ type: 'cloze', attrs: { group: 1 } }], text: 'sympathetic' },
        { type: 'text', text: ' (fight-or-flight) and ' },
        { type: 'text', marks: [{ type: 'cloze', attrs: { group: 2 } }], text: 'parasympathetic' },
        { type: 'text', text: ' (rest-and-digest). They often innervate the same organs with opposing effects.' },
      ],
    },
    {
      type: 'callout',
      attrs: { emoji: '💡' },
      content: [
        {
          type: 'paragraph',
          content: [
            { type: 'text', text: 'Select any word and press ' },
            { type: 'text', marks: [{ type: 'code' }], text: '⌘⇧C' },
            { type: 'text', text: ' to make it a cloze. Type ' },
            { type: 'text', marks: [{ type: 'code' }], text: '/' },
            { type: 'text', text: ' for the block menu.' },
          ],
        },
      ],
    },
    {
      type: 'heading',
      attrs: { level: 3 },
      content: [{ type: 'text', text: 'Key neurotransmitters' }],
    },
    {
      type: 'bulletList',
      content: [
        {
          type: 'listItem',
          content: [
            {
              type: 'paragraph',
              content: [
                { type: 'text', marks: [{ type: 'bold' }], text: 'Acetylcholine' },
                { type: 'text', text: ' — used at all preganglionic synapses and postganglionic parasympathetic targets.' },
              ],
            },
          ],
        },
        {
          type: 'listItem',
          content: [
            {
              type: 'paragraph',
              content: [
                { type: 'text', marks: [{ type: 'bold' }], text: 'Norepinephrine' },
                { type: 'text', text: ' — dominant sympathetic postganglionic transmitter.' },
              ],
            },
          ],
        },
      ],
    },
    {
      type: 'blockquote',
      content: [
        {
          type: 'paragraph',
          content: [
            {
              type: 'text',
              text: 'Acetylcholine is the universal intermediary; norepinephrine is sympathy\'s voice.',
            },
          ],
        },
      ],
    },
  ],
};

export const mockPageCards = [
  { id: 'pc1', n: 1, q: 'Sympathetic branch → fight-or-flight', status: 'due' as const },
  { id: 'pc2', n: 2, q: 'Parasympathetic branch → rest-and-digest', status: 'new' as const },
  { id: 'pc3', n: 3, q: 'Acetylcholine at preganglionic synapses', status: 'ok' as const },
  { id: 'pc4', n: 4, q: 'Norepinephrine — dominant postganglionic sympathetic transmitter', status: 'due' as const },
];

export const mockBacklinks = [
  { id: 'b1', title: 'Beta-blocker mechanisms', count: 3 },
  { id: 'b2', title: 'Parasympathetic ganglia', count: 2 },
  { id: 'b3', title: 'Anticholinergic overdose', count: 1 },
];

export const mockReviewCard: MockReviewCard = {
  id: 'c1',
  pageId: 'd1',
  pageName: 'Kanji — JLPT N3',
  position: 7,
  total: 28,
  kanji: '静',
  prompt: 'What are the primary readings and meaning?',
  hint: 'Radical: blue · 8 strokes',
  readings: [
    { label: 'On', jp: 'セイ', romaji: 'sei', gloss: 'stillness, silence' },
    { label: 'Kun', jp: 'しず', romaji: 'shizu', gloss: 'quiet, still' },
  ],
  example: {
    jp: '図書館はとても静かです。',
    translation: 'The library is very quiet.',
  },
  memoryAid:
    'Imagine a library at dusk — the blue-green light of shelves, each book holding its breath. That hush is 静 (sei / shizu).',
};
